import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import { AddOperationModal } from '@components/Modal/OperationManagement/AddOperation';
import { EditOperationModal } from '@components/Modal/OperationManagement/EditOperation';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import {
  faChevronDown,
  faChevronUp,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  errorMessage,
  onboardingStepId,
  OperationSubsteps,
  responseTexts,
} from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { useEffect, useState } from 'react';
import { OnBoardingCompletion } from '@components/Modal/OnBoardingCompletion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApiEndpoints,
  FrontendRoutes,
  onboardingSteps,
} from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import {
  ModuleActionProps,
  Operation,
  OperationFormProps,
  OperationsModules,
} from '@helpers/Models';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';

export const OperationManagement: React.FC = () => {
  const { showToastMessage } = useToast();
  const { updateOnboardingStep } = useLocalStorage();

  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [isEditingOperation, setIsEditingOperation] = useState(false);
  const [isAddingNewOperation, setIsAddingNewOperation] = useState(false);
  const { showConfirmationToast } = UseConfirmationToast();
  const [showOnBoardingSuccess, setShowOnBoardingSuccess] =
    useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] =
    useState<OperationFormProps | null>(null);
  const navigate = useNavigate();
  const [modules, setModules] = useState<OperationsModules[]>([]);
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;

  useEffect(() => {
    setIsAddingNewOperation(false);
    fetchOperations();
  }, []);

  const fetchOperations = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_OPERATION, {});
      setModules(response.data.data.modules);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const handleResetDefault = async () => {
    try {
      await Axios.post(ApiEndpoints.OPERATIONS_RESET, {});
      fetchOperations();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_RESETTING_DEFAULTS, 'error');
    }
  };

  const editOperation = (
    operation: Operation,
    action: ModuleActionProps,
    module: OperationsModules,
  ): void => {
    const roleIds = operation.OperationRoles.map((role) => role.id);

    const operationForm = {
      id: operation.id,
      operationRoleIds: roleIds,
      name: module.module_name,
      moduleId: module.moduleId,
      message: operation.message,
      moduleActionId: action.moduleActionId,
    };

    setSelectedOperation(operationForm);
    setIsEditingOperation(true);
  };

  const markOnboardingComplete = async (stepName: string) => {
    await Axios.post(ApiEndpoints.UPDATE_ONBOARDING_STEP, {
      stepName,
    });
  };

  const handleOperationDelete = async (id: number) => {
    try {
      await Axios.delete(ApiEndpoints.OPERATION_DELETE, {
        data: { ids: [id] },
      });
      fetchOperations();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_RESETTING_DEFAULTS, 'error');
    }
  };

  const isOnBoardingComplete = (): void => {
    const onboardingSteps = JSON.parse(
      localStorage.getItem('onboardingSteps') || 'false',
    );
    if (onboardingSteps?.[onboardingSteps.length - 1]?.isComplete === 1) {
      showToastMessage(responseTexts.OPERATIONS_SAVE_SUCCESS, 'success');
    } else {
      setShowOnBoardingSuccess(true);
    }
  };

  const renderContent = (
    <>
      {/* Breadcrumbs */}
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Operations"
          activeStep={1}
          subNavSteps={OperationSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={fromSettings ? true : false}
        />
      </div>
      <div className="mt-3 rounded-lg px-4">
        <div className="mb-6">
          {/* Header: Title and Reset button on same row */}
          <div className="flex items-center justify-between">
            <h1 className="font-semibold text-xl mt-3">
              Operations - In house Notifications
            </h1>
            <button
              type="button"
              className="text-primary font-bold mt-3"
              onClick={() =>
                showConfirmationToast(
                  'Reset notifications to default settings?',
                  'Reset to Default',
                  'info',
                  handleResetDefault,
                  'Confirm',
                )
              }
            >
              Reset to Defaults
            </button>
          </div>
          {/* Description placed below */}
          <span className="text-[#000A16B2] block mt-1 w-1/2">
            Configure the notifications that will be sent to your in-house team for various operations. You can edit the message and choose which roles will receive each notification.
          </span>
        </div>
      </div>
      <div className="mb-[115px] px-4">
        {modules.length > 0 ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 w-full items-start">
            {/* LEFT COLUMN */}
            <div className="flex flex-col gap-4 w-full">
              {modules
                .filter((_, idx) => idx % 2 === 0)
                .map((module) => {
                  const originalIndex = modules.findIndex((m) => m.moduleId === module.moduleId);
                  return (
                    <div key={module.moduleId} className="w-full">
                      <div className="border rounded-md shadow-sm bg-[#FFFEFA] h-fit">
                        <button
                          type="button"
                          onClick={() => toggleAccordion(originalIndex)}
                          className="w-full flex justify-between items-center px-4 py-2 font-semibold text-lg"
                        >
                          <span>{module.module_name}</span>
                          <FontAwesomeIcon
                            className="text-[#000A16]"
                            icon={openAccordions.includes(originalIndex) ? faChevronUp : faChevronDown}
                          />
                        </button>

                        {openAccordions.includes(originalIndex) && (
                          <div className="p-2">
                            {module.moduleActions.map((action) => (
                              <div key={action.moduleActionId} className="p-3 bg-white border border-[#0000001F] mb-4">
                                {action.operations.map((operation: Operation) => (
                                  <div key={operation.id}>
                                    <div className="flex justify-between items-center mb-4">
                                      <span className="font-semibold text-lg">{action.moduleActionName}</span>
                                      <div className="flex justify-between items-center gap-4">
                                        <button
                                          onClick={() => editOperation(operation, action, module)}
                                          type="button"
                                          className="text-primary"
                                        >
                                          <FontAwesomeIcon icon={faPen} />
                                          <span className="ml-2 font-semibold">Edit</span>
                                        </button>
                                        <button
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to delete the notification?',
                                              'Delete Notification',
                                              'delete',
                                              () => handleOperationDelete(operation.id),
                                              'Delete',
                                            )
                                          }
                                          type="button"
                                          className="text-primary"
                                        >
                                          <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                      </div>
                                    </div>
                                    <div key={operation.id} className="mb-3">
                                      <div className="flex justify-between mt-2">
                                        <span className="w-[20%] font-semibold">Message:</span>
                                        <span className="w-[80%] text-[#5E5E5E]">{operation.message}</span>
                                      </div>
                                      <div className="mt-2">
                                        <span className="font-semibold">Notification sent to:</span>
                                        <div className="grid grid-cols-2 gap-2 mt-1">
                                          {operation.OperationRoles.map((role) => (
                                            <span key={role.id} className="py-0.5">{role.name}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* RIGHT COLUMN */}
            <div className="flex flex-col gap-4 w-full">
              {modules
                .filter((_, idx) => idx % 2 !== 0)
                .map((module) => {
                  const originalIndex = modules.findIndex((m) => m.moduleId === module.moduleId);
                  return (
                    <div key={module.moduleId} className="w-full">
                      <div className="border rounded-md shadow-sm bg-[#FFFEFA] h-fit">
                        <button
                          type="button"
                          onClick={() => toggleAccordion(originalIndex)}
                          className="w-full flex justify-between items-center px-4 py-2 font-semibold text-lg"
                        >
                          <span>{module.module_name}</span>
                          <FontAwesomeIcon
                            className="text-[#000A16]"
                            icon={openAccordions.includes(originalIndex) ? faChevronUp : faChevronDown}
                          />
                        </button>

                        {openAccordions.includes(originalIndex) && (
                          <div className="p-2">
                            {module.moduleActions.map((action) => (
                              <div key={action.moduleActionId} className="p-3 bg-white border border-[#0000001F] mb-4">
                                {action.operations.map((operation: Operation) => (
                                  <div key={operation.id}>
                                    <div className="flex justify-between items-center mb-4">
                                      <span className="font-semibold text-lg">{action.moduleActionName}</span>
                                      <div className="flex justify-between items-center gap-4">
                                        <button
                                          onClick={() => editOperation(operation, action, module)}
                                          type="button"
                                          className="text-primary"
                                        >
                                          <FontAwesomeIcon icon={faPen} />
                                          <span className="ml-2 font-semibold">Edit</span>
                                        </button>
                                        <button
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to delete the notification?',
                                              'Delete Notification',
                                              'delete',
                                              () => handleOperationDelete(operation.id),
                                              'Delete',
                                            )
                                          }
                                          type="button"
                                          className="text-primary"
                                        >
                                          <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                      </div>
                                    </div>
                                    <div key={operation.id} className="mb-3">
                                      <div className="flex justify-between mt-2">
                                        <span className="w-[20%] font-semibold">Message:</span>
                                        <span className="w-[80%] text-[#5E5E5E]">{operation.message}</span>
                                      </div>
                                      <div className="mt-2">
                                        <span className="font-semibold">Notification sent to:</span>
                                        <div className="grid grid-cols-2 gap-2 mt-1">
                                          {operation.OperationRoles.map((role) => (
                                            <span key={role.id} className="py-0.5">{role.name}</span>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : (
          <div className="mb-[115px] flex flex-col justify-center items-center text-center">
            <img
              src={Assets.NO_DATA_FOUND}
              className="w-[255px] h-[224px] object-fit"
              alt="No Data Found"
            />
            <span className="mt-4">Looks like all operations were deleted!</span>
            <span>You can bring them back by restoring the default settings.</span>
          </div>
        )}
      </div>

      <div className="fixed bottom-6 right-6 mt-3 pt-4 px-4 pb-4 border-gray-100 z-10">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-end gap-4">
          <button
            type="button"
            onClick={() => setIsAddingNewOperation(true)}
            className="lg:w-[160px] xl:w-[170px] h-10 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
          >
            <span>+ Add Operations</span>
          </button>

          {!fromSettings && (
            <div className="w-full xl:w-auto flex items-center justify-between xl:justify-end gap-4">
              <button
                type="button"
                onClick={() =>
                  showToastMessage(
                    responseTexts.OPERATIONS_SAVE_SUCCESS,
                    'success',
                  )
                }
                className="w-1/2 xl:w-[200px] h-11 px-6 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center transition-colors duration-200"
              >
                Save
              </button>

              <button
                type="submit"
                onClick={isOnBoardingComplete}
                className="w-1/2 xl:w-[200px] h-11 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 transition-colors duration-200"
              >
                <span>Complete Onboarding</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="modal-divs">
        {isAddingNewOperation && (
          <AddOperationModal
            isOpen={isAddingNewOperation}
            onClose={() => {
              setIsAddingNewOperation(false);
              fetchOperations();
            }}
            modules={modules}
          />
        )}
        {selectedOperation && (
          <EditOperationModal
            isOpen={isEditingOperation}
            onClose={() => {
              setIsEditingOperation(false);
              fetchOperations();
            }}
            operation={selectedOperation}
          />
        )}
        {showOnBoardingSuccess && (
          <OnBoardingCompletion
            onAction={() => {
              markOnboardingComplete(onboardingSteps.OPERATION_MANAGEMENT);
              updateOnboardingStep(onboardingStepId.OPERATION);
              navigate(FrontendRoutes.CUSTOMER_LIST);
            }}
          />
        )}
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} />
      )}
    </>
  );
};